import { Card } from '@ohear/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { AdminGuard } from '../../features/admin/AdminGuard'
import { AdminNav } from '../../features/admin/AdminNav'

export const Route = createFileRoute('/admin/')({
  component: AdminHome,
})

interface QuickLink {
  to: string
  title: string
  body: string
}

const QUICK_LINKS: readonly QuickLink[] = [
  {
    to: '/admin/orgs',
    title: 'Organizations',
    body: 'Browse every tenant. Suspend or restore an org without leaving this surface.',
  },
  {
    to: '/admin/failed-jobs',
    title: 'Failed jobs',
    body: 'Inspect the dead-letter queue. Replay a job with its original input or mark it resolved.',
  },
  {
    to: '/admin/feature-flags',
    title: 'Feature flags',
    body: 'Toggle defaults, enable an org explicitly, or roll out by percentage.',
  },
  {
    to: '/admin/audit-log',
    title: 'Audit log',
    body: 'Filter the immutable trail by org, user, action, or time window.',
  },
  {
    to: '/admin/archival',
    title: 'Archival',
    body: 'Fast-track delivered projects to cold tier or restore them. The daily worker handles the routine 90-day case.',
  },
  {
    to: '/admin/health',
    title: 'Health',
    body: 'Aggregate client-side error volume from Workers Analytics Engine. Quick-glance dashboard, not the triage queue.',
  },
] as const

function AdminHome() {
  return (
    <AdminGuard>
      <section aria-labelledby="admin-home-title" className="flex flex-col gap-3">
        <header className="flex flex-col gap-1">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">Internal</div>
          <h1 id="admin-home-title" className="text-[22px] font-semibold tracking-tight m-0">
            Platform admin
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            Internal tools for OHEAR.AI staff. Every action here is recorded in the audit log.
          </p>
        </header>

        <AdminNav />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {QUICK_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className="no-underline group">
              <Card
                title={
                  <span className="inline-flex items-center gap-2">
                    {link.title}
                    <span
                      className="text-[var(--text-3)] transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    >
                      →
                    </span>
                  </span>
                }
                className="h-full transition-all hover:border-[var(--accent)] hover:-translate-y-0.5"
              >
                <p className="text-[13px] text-[var(--text-2)] m-0">{link.body}</p>
              </Card>
            </Link>
          ))}
        </div>
      </section>
    </AdminGuard>
  )
}
